import { RegistroCta } from '@/features/auth/components/registro-cta';
import {
  FileText,
  Calculator,
  ShieldCheck,
  Bot,
  BarChart3,
  Package,
  Users,
  ArrowRight,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { DocsLayout } from '../components/docs-layout';

const productSections = [
  {
    icon: FileText,
    title: 'Que es Prixia',
    gradient: 'from-blue-500 to-cyan-400',
    content: `Prixia es una plataforma de inteligencia de precios disenada para empresas que necesitan profesionalizar y automatizar su proceso de cotizacion. La plataforma permite a equipos de ventas crear cotizaciones complejas en minutos en lugar de horas, eliminar errores manuales y tomar decisiones de pricing basadas en datos reales.

La solucion esta construida como una plataforma multi-tenant, lo que significa que cada empresa tiene su propio espacio completamente aislado con sus datos, usuarios, modelos de pricing y configuraciones independientes. Esto garantiza la privacidad y seguridad entre organizaciones.

Con Prixia, las empresas pueden definir modelos de pricing con formulas dinamicas, generar cotizaciones multi-seccion, gestionar flujos de aprobacion automatizados, y obtener insights de pricing a traves de analytics avanzados y un asistente de IA integrado.`,
  },
  {
    icon: Calculator,
    title: 'Modelos de Pricing',
    gradient: 'from-violet-500 to-purple-400',
    content: `Los modelos de pricing son el corazon de la plataforma. Son plantillas de calculo reutilizables que definen como se determinan los precios de tus productos y servicios.

Cada modelo tiene un nombre identificador, un codigo unico, un margen objetivo (el margen ideal de ganancia) y un margen piso (el minimo aceptable). Dentro del modelo se definen variables de distintos tipos que automatizan los calculos de precios.

Tipos de variables disponibles:
- NUMBER: Para cantidades numericas como usuarios, horas o unidades.
- CURRENCY: Para valores monetarios como costo unitario o precio base.
- PERCENTAGE: Para porcentajes como descuentos, tasas impositivas o margenes.
- TEXT: Para informacion textual como notas o referencias.
- BOOLEAN: Para valores de Si/No, ideal para modulos opcionales.
- CALCULATED: Formulas automaticas que referencian otras variables por su codigo. Por ejemplo: PRECIO_FINAL = COSTO_BASE * (1 + MARGEN / 100).
- ENUM: Lista desplegable con opciones predefinidas, como niveles de servicio (Basico, Premium, Enterprise).
- TIERED: Precios escalonados por volumen. Define rangos de cantidad y el precio correspondiente para cada rango.
- LOOKUP: Tablas de busqueda 2D para encontrar valores segun dos dimensiones, como pais y nivel de servicio.

Las variables pueden ser globales (el valor se comparte en toda la seccion de la cotizacion) o por item (cada linea tiene su propio valor). El sistema valida formulas en tiempo real, detecta errores de sintaxis y previene dependencias circulares.`,
  },
  {
    icon: FileText,
    title: 'Cotizaciones',
    gradient: 'from-emerald-500 to-green-400',
    content: `El modulo de cotizaciones permite crear propuestas comerciales profesionales siguiendo un flujo de 4 pasos:

Paso 1 - Informacion del Cliente: Ingresa los datos del cliente (nombre, email, telefono) y opcionalmente un ID de oportunidad para integracion con SAP C4C.

Paso 2 - Configuracion: Selecciona la linea de negocio (estructura jerarquica de 3 niveles), el pais, la moneda de la cotizacion y los dias de validez.

Paso 3 - Secciones de Pricing (Multi-Pricer): Esta es la caracteristica mas potente. Una cotizacion puede tener multiples secciones, cada una usando un modelo de pricing diferente. Por ejemplo, puedes combinar en una sola cotizacion: Licenciamiento SaaS + Venta de Hardware + Soporte Gestionado. Para cada seccion seleccionas el modelo, configuras las variables globales y agregas lineas de items vinculados a materiales del catalogo.

Paso 4 - Revision y Envio: Se muestra la cascada de costos completa con indicadores visuales de margen: verde (supera el objetivo), amarillo (entre piso y objetivo) y rojo (por debajo del piso, requiere justificacion).

Las cotizaciones pasan por estados: Borrador, Pendiente de Aprobacion, Aprobada, Rechazada, Ajuste Solicitado, Vencida y Congelada. Puedes clonar cotizaciones existentes y exportar a PDF profesional.`,
  },
  {
    icon: ShieldCheck,
    title: 'Workflow de Aprobacion',
    gradient: 'from-orange-500 to-amber-400',
    content: `El sistema de aprobaciones asegura que toda cotizacion sea revisada antes de llegar al cliente. Cuando un vendedor envia una cotizacion para aprobacion, esta aparece automaticamente en el panel de aprobaciones de los usuarios con rol Aprobador o Administrador de Empresa.

El panel muestra estadisticas en tiempo real: cotizaciones pendientes, aprobadas, rechazadas y el tiempo promedio de resolucion. Para cada cotizacion pendiente se visualiza el codigo, cliente, vendedor, precio final, margen actual y fecha de solicitud.

El aprobador tiene tres opciones:
- Aprobar: Con un comentario opcional. La cotizacion pasa a estado Aprobada y el vendedor recibe notificacion.
- Rechazar: Con un comentario obligatorio explicando los motivos. La cotizacion se marca como Rechazada.
- Solicitar Ajuste: Con un precio sugerido y comentario obligatorios. La cotizacion vuelve al vendedor con un banner visible mostrando la sugerencia del aprobador.

El flujo de ajuste permite multiples iteraciones: el vendedor modifica la cotizacion segun la retroalimentacion, la reenvia para aprobacion, y el aprobador vuelve a evaluar. Todas las notificaciones se gestionan automaticamente.`,
  },
  {
    icon: Bot,
    title: 'Asistente de IA',
    gradient: 'from-pink-500 to-rose-400',
    content: `El Asistente de IA esta disponible como un boton flotante en todas las paginas de la plataforma, ofreciendo soporte contextual e inteligente a los usuarios.

Funcionalidades principales:
- Responde preguntas sobre el uso del sistema y guia a los usuarios en tareas complejas.
- Explica conceptos de pricing, margenes y estrategias de venta.
- Proporciona sugerencias de preguntas frecuentes predefinidas para un inicio rapido.
- Tiene contexto completo del dominio de la plataforma para dar respuestas precisas.

El asistente esta potenciado por la API de Claude de Anthropic, lo que le permite entender consultas en espanol y proporcionar respuestas inteligentes y contextuales. Si la empresa no tiene configurada una API key, se activa un modo basico con respuestas predefinidas para las consultas mas comunes.

Los datos del usuario permanecen seguros: las conversaciones no se almacenan ni se usan para entrenar modelos. Cada consulta se procesa en tiempo real y los datos del tenant permanecen aislados.`,
  },
  {
    icon: BarChart3,
    title: 'Analytics y Dashboard',
    gradient: 'from-sky-500 to-indigo-400',
    content: `Al iniciar sesion, cada usuario accede a un Dashboard personalizado segun su rol con accesos rapidos a las funciones principales.

El modulo de Analytics avanzado proporciona metricas clave para tomar decisiones informadas:
- Win Rate: Porcentaje de cotizaciones aprobadas versus el total.
- Ticket Promedio: Valor medio de las cotizaciones generadas.
- Tendencias por Vendedor: Desempeno individual de cada miembro del equipo de ventas.
- Tendencias por Linea de Negocio: Analisis de rentabilidad por area de negocio.
- Margenes Promedio: Seguimiento del margen de ganancia a lo largo del tiempo.
- Tiempo de Resolucion: Cuanto tarda el proceso desde la creacion hasta la aprobacion.

Los dashboards se actualizan en tiempo real y se adaptan segun el rol del usuario. Los administradores ven metricas globales de la empresa, mientras que los vendedores ven su desempeno individual. Las graficas interactivas permiten filtrar por periodo, vendedor y categoria.`,
  },
  {
    icon: Package,
    title: 'Gestion de Materiales',
    gradient: 'from-teal-500 to-emerald-400',
    content: `El catalogo de materiales centraliza todos los productos y servicios que ofrece tu empresa, facilitando la creacion de cotizaciones consistentes y precisas.

Cada material se registra con:
- Codigo interno: Identificador unico dentro de tu organizacion.
- Codigo externo: Referencia del proveedor o fabricante.
- Nombre: Descripcion del producto o servicio.
- Partida arancelaria: Clasificacion aduanera para materiales importados.
- Tasa arancelaria activa: Porcentaje vigente con historial de cambios.

El sistema mantiene un historial completo de tasas arancelarias con fecha de vigencia. Solo una tasa puede estar activa a la vez, y cuando se crea una cotizacion, la tasa activa del material se aplica automaticamente en los calculos. Esto es especialmente util para empresas importadoras que manejan productos con aranceles variables.

Los materiales se vinculan a las lineas de items en las cotizaciones, permitiendo seleccionar rapidamente productos del catalogo y heredar su informacion arancelaria.`,
  },
  {
    icon: Users,
    title: 'Gestion de Clientes',
    gradient: 'from-amber-500 to-yellow-400',
    content: `El modulo de gestion de clientes permite mantener una base de datos centralizada de todos los clientes de tu empresa, con acceso al historial completo de cotizaciones de cada uno.

Funcionalidades principales:
- Registro de clientes con informacion de contacto completa (nombre, email, telefono, empresa).
- Historial de cotizaciones: Visualiza todas las cotizaciones enviadas a cada cliente, con su estado actual y valores.
- Seguimiento de relaciones comerciales: Identifica clientes con mayor volumen, mejores margenes y oportunidades de upsell.
- Busqueda y filtrado avanzado para encontrar clientes rapidamente.

La gestion de clientes se integra directamente con el flujo de cotizacion: al crear una nueva cotizacion, puedes seleccionar un cliente existente o registrar uno nuevo. Esto agiliza el proceso y mantiene la consistencia de datos.

Para empresas que usan SAP Cloud for Customer (C4C), el campo de ID de oportunidad permite vincular las cotizaciones directamente con las oportunidades del CRM, facilitando el seguimiento del pipeline de ventas.`,
  },
];

export function ProductGuidePage() {
  return (
    <DocsLayout
      title="Guia de Producto"
      description="Conoce todas las funcionalidades de Prixia y como pueden transformar tu proceso de pricing."
    >
      <div className="space-y-8">
        {productSections.map((section, idx) => (
          <Card
            key={idx}
            className="overflow-hidden border border-gray-200 dark:border-gray-800 hover:shadow-lg transition-shadow"
          >
            <CardContent className="p-0">
              <div className="flex items-start gap-4 p-6 sm:p-8">
                <div
                  className={`shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${section.gradient} flex items-center justify-center`}
                >
                  <section.icon className="w-6 h-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                    {section.title}
                  </h2>
                  <div className="space-y-3">
                    {section.content.split('\n\n').map((paragraph, pIdx) => {
                      if (paragraph.trim().startsWith('- ')) {
                        const items = paragraph
                          .split('\n')
                          .filter((line) => line.trim().startsWith('- '));
                        return (
                          <ul key={pIdx} className="space-y-2 pl-1">
                            {items.map((item, iIdx) => (
                              <li
                                key={iIdx}
                                className="flex items-start gap-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400"
                              >
                                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500/60" />
                                <span>{item.replace(/^-\s*/, '')}</span>
                              </li>
                            ))}
                          </ul>
                        );
                      }

                      const lines = paragraph.split('\n');
                      if (
                        lines.length > 1 &&
                        lines[0].trim().endsWith(':') &&
                        !lines[0].trim().startsWith('-')
                      ) {
                        const heading = lines[0].trim();
                        const bodyLines = lines.slice(1);
                        const hasListItems = bodyLines.some((l) => l.trim().startsWith('-'));

                        return (
                          <div key={pIdx}>
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                              {heading}
                            </h3>
                            {hasListItems ? (
                              <ul className="space-y-2 pl-1">
                                {bodyLines
                                  .filter((l) => l.trim().startsWith('-'))
                                  .map((item, iIdx) => (
                                    <li
                                      key={iIdx}
                                      className="flex items-start gap-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400"
                                    >
                                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500/60" />
                                      <span>{item.replace(/^-\s*/, '')}</span>
                                    </li>
                                  ))}
                              </ul>
                            ) : (
                              <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                                {bodyLines.join(' ').trim()}
                              </p>
                            )}
                          </div>
                        );
                      }

                      return (
                        <p
                          key={pIdx}
                          className="text-sm leading-relaxed text-gray-600 dark:text-gray-400"
                        >
                          {paragraph}
                        </p>
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* CTA */}
        <div className="mt-12 text-center py-12 px-6 rounded-2xl bg-gradient-to-r from-blue-50 to-violet-50 dark:from-blue-950/30 dark:to-violet-950/30 border border-blue-100 dark:border-blue-900/50">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Listo para transformar tu pricing?
          </h2>
          <p className="mt-2 text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
            Comienza tu prueba gratuita de 30 dias con acceso completo a todas las funcionalidades.
          </p>
          <RegistroCta
            className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-violet-600 px-6 py-3 rounded-lg hover:shadow-lg transition-shadow"
          >
            Crear Cuenta Gratis
            <ArrowRight className="w-4 h-4" />
          </RegistroCta>
        </div>
      </div>
    </DocsLayout>
  );
}
